import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Landmark, Percent, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#15151A] border border-[#D4AF37]/20 rounded-xl p-5 flex-1" data-testid={testid}>
    <Icon className="w-5 h-5 text-[#D4AF37] mb-2" strokeWidth={1.5} />
    <p className="text-xs uppercase tracking-widest text-[#A0A0A0]">{label}</p>
    <p className="font-serif text-2xl text-white mt-1">{value}</p>
  </div>
);

export const LedgerTab = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/ledger")
      .then((r) => setData(r.data))
      .catch(() => {
        setData({ rows: [], totals: { collected: 0, commission: 0, refundable: 0, refunded_count: 0 } });
        toast.error("Could not load ledger");
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refund = async (depositId) => {
    setBusy(depositId);
    try {
      await api.post(`/admin/deposits/${depositId}/refund`);
      toast.success("Deposit marked as refunded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark refund");
    } finally {
      setBusy(null);
    }
  };

  if (data === null) return <Skeleton className="h-40 w-full rounded-xl bg-[#15151A]" />;
  const { rows, totals } = data;

  return (
    <div className="space-y-6" data-testid="admin-ledger-tab">
      <div className="flex flex-col sm:flex-row gap-4">
        <Stat icon={Landmark} label="Total Collected" value={inr(totals.collected)} testid="ledger-total-collected" />
        <Stat icon={Percent} label="Commission (5%)" value={inr(totals.commission)} testid="ledger-total-commission" />
        <Stat icon={Undo2} label="Total Refundable (95%)" value={inr(totals.refundable)} testid="ledger-total-refundable" />
      </div>

      <div className="bg-[#15151A] border border-[#D4AF37]/20 rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Payer</TableHead>
              <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Plan</TableHead>
              <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Amount</TableHead>
              <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Commission</TableHead>
              <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Refundable</TableHead>
              <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Refund</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className="border-white/5">
                <TableCell colSpan={6} className="text-center text-sm text-[#A0A0A0] py-10" data-testid="ledger-empty">
                  No approved deposits yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.deposit_id} className="border-white/5 hover:bg-white/5" data-testid="ledger-row">
                  <TableCell>
                    <p className="text-sm text-white">{r.payer_name}</p>
                    <p className="text-[10px] uppercase tracking-widest text-[#A0A0A0]">
                      {r.role} · <span data-testid="ledger-method">{r.method === "razorpay" ? "Razorpay" : "Manual UPI"}</span>
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-[#E0E0E0]">{r.plan_title}</p>
                    <p className="text-xs text-[#A0A0A0]">{r.plan_status.replace("_", " ")}</p>
                  </TableCell>
                  <TableCell className="text-sm text-[#D4AF37]" data-testid="ledger-amount">{inr(r.amount)}</TableCell>
                  <TableCell className="text-sm text-[#E0E0E0]" data-testid="ledger-commission">{inr(r.commission)}</TableCell>
                  <TableCell className="text-sm text-[#E0E0E0]" data-testid="ledger-refundable">{inr(r.refundable)}</TableCell>
                  <TableCell>
                    {r.refunded ? (
                      <Badge className="bg-transparent border border-[#4CAF50]/60 text-[#4CAF50] text-[10px] uppercase" data-testid="ledger-refunded-badge">
                        Refunded
                      </Badge>
                    ) : r.refund_eligible ? (
                      <Button
                        size="sm"
                        disabled={busy === r.deposit_id}
                        onClick={() => refund(r.deposit_id)}
                        data-testid="ledger-refund-button"
                        className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold transition-colors"
                      >
                        Mark Refunded
                      </Button>
                    ) : (
                      <span className="text-xs text-[#5a5a66] italic">Awaiting completion</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
